import { themeAtom } from "../../../shared/atoms";
import type { AdminCrewReportBulkRequestStatusModalProps } from "../../models/admin-crew-report.models";
import { adminStyles } from "../../styles/admin.styles";
import AdminCrewReportBulkRequestStatusRow from "./AdminCrewReportBulkRequestStatusRow";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportBulkRequestStatusModal = ({
  items,
  runStatus,
  isUpdatingStatuses,
  onClose,
  onStop,
  onResume,
}: AdminCrewReportBulkRequestStatusModalProps) => {
  const theme = useAtomValue(themeAtom);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const isClosing = runStatus === "closing";
  const isStopping = runStatus === "stopping";

  return (
    <Dialog
      as="div"
      open
      initialFocus={titleRef}
      onClose={() => undefined}
      data-test="admin-crew-report-bulk-request-status-modal"
      className="fixed inset-0 z-50"
    >
      <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          role="document"
          aria-label="Bulk Request Status content"
          className={twMerge(
            "flex max-h-[92vh] w-[840px] max-w-[calc(100vw-1.5rem)] flex-col bg-white shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-[#D8E6E3] px-5 py-4 dark:border-gray-700">
            <Dialog.Title ref={titleRef} tabIndex={-1} className="text-2xl font-semibold outline-none">
              Bulk Request Status
            </Dialog.Title>
            <button
              type="button"
              disabled={isClosing}
              aria-label="Close Bulk Request Status"
              className="p-2 text-[#596563] hover:bg-[#E9F4F3] disabled:cursor-wait disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </header>

          <div className="min-h-0 overflow-x-auto px-5 py-4">
            <div
              role="table"
              aria-label="Bulk Crew Report request progress"
              className="min-w-[780px] border border-[#D8E6E3] dark:border-gray-700"
            >
              <div
                role="row"
                className="grid grid-cols-[minmax(130px,0.855fr)_90px_minmax(260px,1.35fr)_180px] bg-[#F2F7F5] text-[11px] font-semibold uppercase text-[#4A5655] dark:bg-gray-900 dark:text-gray-300"
              >
                <div role="columnheader" className="px-3 py-2">
                  Vessel
                </div>
                <div role="columnheader" className="px-3 py-2">
                  IMO
                </div>
                <div role="columnheader" className="px-3 py-2">
                  Email
                </div>
                <div role="columnheader" className="w-[180px] px-3 py-2">
                  Status
                </div>
              </div>
              <div
                role="rowgroup"
                data-test="admin-crew-report-bulk-request-status-list"
                className="max-h-[420px] overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5"
              >
                {items.map((item) => (
                  <AdminCrewReportBulkRequestStatusRow key={item.vessel.imoNumber} item={item} />
                ))}
              </div>
            </div>
          </div>

          <footer className="flex items-center justify-between gap-4 border-t border-[#D8E6E3] px-5 py-4 dark:border-gray-700">
            {isUpdatingStatuses ? (
              <span className="text-sm font-medium text-[#4A5655] dark:text-gray-300" role="status" aria-live="polite">
                Updating Crew Report request statuses.
              </span>
            ) : (
              <span aria-hidden="true" />
            )}
            <div className="flex flex-none justify-end">
              {runStatus === "paused" ? (
                <button
                  type="button"
                  className={twMerge(adminStyles.button.primary, adminStyles.button.safe, "w-auto")}
                  onClick={onResume}
                >
                  Resume Bulk Requests
                </button>
              ) : runStatus === "completed" ? (
                <span className="text-sm font-semibold text-green" role="status">
                  Bulk Requests Completed
                </span>
              ) : (
                <button
                  type="button"
                  disabled={isStopping || isClosing}
                  className={twMerge(adminStyles.button.primary, adminStyles.button.live, "w-auto")}
                  onClick={onStop}
                >
                  {isStopping || isClosing ? "Stopping Bulk Requests" : "Stop Bulk Requests"}
                </button>
              )}
            </div>
          </footer>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminCrewReportBulkRequestStatusModal;
